import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useEnvStore } from "@/os/env/useEnvStore";
import { sandboxIdentityForPath } from "./sandboxUser";
import { isSandboxAllowedHere } from "./sandboxGuard";
import { setMonitoringUser } from "@/monitoring/sentry";

export type AppRole = "admin" | "operator" | "client" | "prospect";

type AuthCtx = {
  user: User | null;
  session: Session | null;
  roles: AppRole[];
  primaryRole: AppRole | null;
  loading: boolean;
  signOut: () => Promise<void>;
  refresh: () => Promise<void>;
};

const Ctx = createContext<AuthCtx | undefined>(undefined);

const roleRank: Record<AppRole, number> = { admin: 1, operator: 2, client: 3, prospect: 4 };

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);
  const [loading, setLoading] = useState(true);
  // Tracks whether we've finished resolving roles for the current user.
  // Without this flag, on the very first login `loading` flips to false
  // (from the initial getSession() with no session) before the post-OTP
  // onAuthStateChange has had a chance to load user_roles — which causes
  // OSRedirect/RequireRole to bounce in a redirect loop (blank screen).
  const [rolesLoaded, setRolesLoaded] = useState(false);

  const loadRoles = async (uid: string) => {
    const { data } = await supabase.from("user_roles").select("role").eq("user_id", uid);
    setRoles(((data ?? []) as { role: AppRole }[]).map((r) => r.role));
    setRolesLoaded(true);
    // Hard block: if this user has been marked as inactive in user_permissions,
    // sign them out immediately. Active is the default when no row exists.
    try {
      const { data: perm } = await supabase
        .from("user_permissions")
        .select("status")
        .eq("user_id", uid)
        .maybeSingle();
      if (perm && (perm as any).status === "inactive") {
        await supabase.auth.signOut();
        return;
      }
    } catch {
      // Silent: best-effort guard; RLS still protects data access.
    }
    // Hard block: if every linked client record is inactive, sign the user
    // out immediately. Admins/operators are not affected because they don't
    // own client rows. Runs in production env only (sandbox bypasses auth).
    try {
      const { data: linkedClients } = await supabase
        .from("clients")
        .select("status")
        .eq("user_id", uid);
      if ((linkedClients ?? []).length > 0 && (linkedClients ?? []).every((c: any) => c.status === "inactive")) {
        await supabase.auth.signOut();
      }
    } catch {
      // Silent: blocking on inactive is best-effort; if the query fails the
      // RLS policies still protect data access.
    }
  };

  const refresh = async () => {
    if (user) await loadRoles(user.id);
  };

  useEffect(() => {
    let currentUserId: string | null = null;
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, s) => {
      setSession(s);
      setUser(s?.user ?? null);
      const nextUid = s?.user?.id ?? null;
      if (nextUid) {
        // Only re-resolve roles when the user actually changes.
        // Skipping this on TOKEN_REFRESHED / SIGNED_IN events that fire
        // when the browser tab regains focus prevents `loading` from
        // flipping back to true, which would unmount the current view
        // (e.g. reset the QuoteWizard slide back to step 1).
        if (nextUid !== currentUserId) {
          currentUserId = nextUid;
          setRolesLoaded(false);
          setTimeout(() => loadRoles(nextUid), 0);
        }
      } else {
        currentUserId = null;
        setRoles([]);
        setRolesLoaded(true);
      }
    });
    supabase.auth.getSession().then(({ data: { session: s } }) => {
      setSession(s);
      setUser(s?.user ?? null);
      if (s?.user) {
        currentUserId = s.user.id;
        loadRoles(s.user.id).finally(() => setLoading(false));
      }
      else {
        setRolesLoaded(true);
        setLoading(false);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const primaryRole =
    roles.length === 0
      ? null
      : roles.slice().sort((a, b) => roleRank[a] - roleRank[b])[0];

  // Contexto de observabilidad: quién es el usuario afectado y con qué rol,
  // para poder contactarlo y reproducir el error.
  useEffect(() => {
    if (user) {
      setMonitoringUser({ id: user.id, email: user.email ?? null, role: primaryRole });
    } else {
      setMonitoringUser(null);
    }
  }, [user?.id, user?.email, primaryRole]);


  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <Ctx.Provider
      value={{
        user,
        session,
        roles,
        primaryRole,
        // Treat "user present but roles not yet loaded" as still loading,
        // so RequireRole/OSRedirect don't bounce on a brand-new session.
        loading: loading || (!!user && !rolesLoaded),
        signOut,
        refresh,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside AuthProvider");
  // In sandbox, we simulate a different user per hub. Hooks must always
  // run in the same order, so call them unconditionally.
  const env = useEnvStore((s) => s.env);
  const loc = useLocation();
  if (env === "sandbox" && isSandboxAllowedHere()) {
    const identity = sandboxIdentityForPath(loc.pathname);
    return {
      ...v,
      user: identity.user,
      session: null,
      roles: identity.roles,
      primaryRole: identity.roles[0] ?? null,
      loading: false,
    };
  }
  return v;
}